/**
 * Audio on a signal trace: traced mono, shown stereo
 * (docs/signal-flow-tracing-plan.md, "Audio: trace mono, show stereo").
 *
 * Every channel is traced on its own (`signal-trace-tree.ts`). The start
 * port defines the pairs (1+2, 3+4, ...); the two trees of a pair are walked
 * side by side and drawn as one lane while they run the same way, only the
 * channel number differing. Where they part, the lane forks into an L and an
 * R lane. Pairs are never guessed from Dante: only what the two channels do
 * decides.
 */
import { traceLeaves } from "./signal-trace-tree.js";
/** Leaves of all trees are `empty`: the pair carries nothing. */
function carriesNothing(trees) {
    return trees.every((tree) => traceLeaves(tree).every((leaf) => leaf.status === "empty"));
}
function identity(node) {
    // Channels may differ; Dante channel labels carry their names, so a
    // Dante hop is identified by its device and direction only
    return [
        node.deviceID,
        node.portID ?? "",
        node.danteChannel
            ? `${node.danteChannel.device}/${node.danteChannel.direction}`
            : node.portID
                ? ""
                : node.label,
        node.status,
        node.reason ?? "",
        node.link?.id ?? "",
    ].join("|");
}
/** Both sides are on the very same channel of a port or Dante device. */
function sameChannel(a, b) {
    if (a.channel === undefined && !a.danteChannel)
        return false;
    return (a.channel === b.channel && a.danteChannel?.index === b.danteChannel?.index);
}
function base(node) {
    return {
        deviceID: node.deviceID,
        ...(node.portID ? { portID: node.portID } : {}),
        label: node.label,
        status: node.status,
        ...(node.reason ? { reason: node.reason } : {}),
        ...(node.stateFrom ? { stateFrom: node.stateFrom } : {}),
        ...(node.link ? { link: node.link } : {}),
        ...(node.embedding ? { embedding: node.embedding } : {}),
    };
}
/** One channel's tree as a lane, on side `side` (or alone). */
export function singleLane(node, side = "L") {
    return {
        ...base(node),
        ...(node.channel !== undefined
            ? { channels: { [side]: node.channel } }
            : {}),
        ...(node.danteChannel
            ? { danteChannels: { [side]: node.danteChannel } }
            : {}),
        ...(node.side ? { sides: { [side]: node.side } } : {}),
        feeds: node.feeds.map((feed) => ({
            ...(feed.role ? { role: feed.role } : {}),
            node: singleLane(feed.node, side),
        })),
    };
}
/** Walk the trees of a pair side by side; they share identity here. */
function mergePair(left, right) {
    const node = {
        ...base(left),
        ...(left.channel !== undefined || right.channel !== undefined
            ? { channels: { L: left.channel, R: right.channel } }
            : {}),
        ...(left.danteChannel || right.danteChannel
            ? { danteChannels: { L: left.danteChannel, R: right.danteChannel } }
            : {}),
        ...(left.side || right.side
            ? { sides: { L: left.side, R: right.side } }
            : {}),
        ...(sameChannel(left, right) ? { mono: true } : {}),
        feeds: [],
    };
    const together = left.feeds.length === right.feeds.length &&
        left.feeds.every((feed, i) => feed.role === right.feeds[i].role &&
            identity(feed.node) === identity(right.feeds[i].node));
    if (together) {
        node.feeds = left.feeds.map((feed, i) => ({
            ...(feed.role ? { role: feed.role } : {}),
            node: mergePair(feed.node, right.feeds[i].node),
        }));
        return node;
    }
    node.fork = true;
    node.feeds = [
        ...left.feeds.map((feed) => ({
            role: "L",
            node: singleLane(feed.node, "L"),
        })),
        ...right.feeds.map((feed) => ({
            role: "R",
            node: singleLane(feed.node, "R"),
        })),
    ];
    return node;
}
function* laneNodes(node) {
    yield node;
    for (const feed of node.feeds)
        yield* laneNodes(feed.node);
}
function laneWarnings(tree) {
    const warnings = [];
    for (const node of laneNodes(tree)) {
        if (node.fork) {
            warnings.push({
                kind: "split",
                text: `Left and right go different ways after ${node.label}`,
            });
        }
        const sides = node.sides;
        if (node.mono || !sides?.L || !sides.R)
            continue;
        if (sides.L === "L" && sides.R === "R")
            continue;
        warnings.push({
            kind: "swapped",
            text: sides.L === sides.R
                ? `Left and right swapped: both are ${node.label} ${sides.L}`
                : `Left and right swapped at ${node.label}`,
        });
    }
    return warnings;
}
/**
 * The audio lanes of a port from its channel trees (`audio[0]` is channel
 * 1): one per pair (1+2, 3+4, ...), a last odd channel alone. Lanes whose
 * channels all end `empty` are marked `empty` (the map hides them).
 */
export function audioLanes(audio) {
    const lanes = [];
    for (let i = 0; i < audio.length; i += 2) {
        const left = audio[i];
        const right = audio[i + 1];
        if (!right) {
            lanes.push({
                channels: [i + 1],
                label: String(i + 1),
                tree: singleLane(left),
                mono: true,
                warnings: [],
                empty: carriesNothing([left]),
            });
            continue;
        }
        const tree = identity(left) === identity(right)
            ? mergePair(left, right)
            : {
                ...base(left),
                fork: true,
                feeds: [
                    { role: "L", node: singleLane(left, "L") },
                    { role: "R", node: singleLane(right, "R") },
                ],
            };
        lanes.push({
            channels: [i + 1, i + 2],
            label: `${i + 1}+${i + 2}`,
            tree,
            mono: [...laneNodes(tree)].some((node) => node.mono),
            warnings: laneWarnings(tree),
            empty: carriesNothing([left, right]),
        });
    }
    return lanes;
}
/** A video tree as a lane (the same shape, no channels). */
export function videoLane(video) {
    return singleLane(video);
}
